/**
 * Nutritional data models for Ciqual database
 * Based on the French food composition table structure
 */

// Basic food identification
export interface FoodIdentification {
  /** Food group code */
  alim_grp_code: string;
  /** Food subgroup code */
  alim_ssgrp_code: string;
  /** Food sub-subgroup code */
  alim_ssssgrp_code: string;
  /** Unique food code */
  alim_code: number;
  /** French name of the food */
  alim_nom_fr: string;
}

// Macronutrients (per 100g)
export interface Macronutrients {
  /** Water content (g/100g) */
  water: number;
  /** Proteins with Jones factor (g/100g) */
  proteins_jones: number;
  /** Proteins with 6.25 factor (g/100g) */
  proteins_625: number;
  /** Carbohydrates (g/100g) */
  carbohydrates: number;
  /** Lipids/Fats (g/100g) */
  lipids: number;
  /** Sugars (g/100g) */
  sugars: number;
  /** Dietary fibers (g/100g) */
  dietary_fibers: number;
  /** Ash content (g/100g) */
  ash: number;
  /** Alcohol (g/100g) */
  alcohol: number;
  /** Organic acids (g/100g) */
  organic_acids: number;
}

// Specific sugars (per 100g)
export interface Sugars {
  /** Fructose (g/100g) */
  fructose: number;
  /** Glucose (g/100g) */
  glucose: number;
  /** Lactose (g/100g) */
  lactose: number;
  /** Maltose (g/100g) */
  maltose: number;
  /** Sucrose (g/100g) */
  sucrose: number;
  /** Starch (g/100g) */
  starch: number;
}

// Fatty acids (per 100g)
export interface FattyAcids {
  /** Saturated fatty acids (g/100g) */
  saturated: number;
  /** Monounsaturated fatty acids (g/100g) */
  monounsaturated: number;
  /** Polyunsaturated fatty acids (g/100g) */
  polyunsaturated: number;
  /** Butyric acid 4:0 (g/100g) */
  butyric_4_0: number;
  /** Caproic acid 6:0 (g/100g) */
  caproic_6_0: number;
  /** Caprylic acid 8:0 (g/100g) */
  caprylic_8_0: number;
  /** Capric acid 10:0 (g/100g) */
  capric_10_0: number;
  /** Lauric acid 12:0 (g/100g) */
  lauric_12_0: number;
  /** Myristic acid 14:0 (g/100g) */
  myristic_14_0: number;
  /** Palmitic acid 16:0 (g/100g) */
  palmitic_16_0: number;
  /** Stearic acid 18:0 (g/100g) */
  stearic_18_0: number;
  /** Oleic acid 18:1 9c (n-9) (g/100g) */
  oleic_18_1_9c: number;
  /** Linoleic acid 18:2 9c,12c (n-6) (g/100g) */
  linoleic_18_2_9c_12c: number;
  /** Alpha-linolenic acid 18:3 c9,c12,c15 (n-3) (g/100g) */
  alpha_linolenic_18_3: number;
  /** Arachidonic acid 20:4 5c,8c,11c,14c (n-6) (g/100g) */
  arachidonic_20_4: number;
  /** EPA 20:5 5c,8c,11c,14c,17c (n-3) (g/100g) */
  epa_20_5: number;
  /** DHA 22:6 4c,7c,10c,13c,16c,19c (n-3) (g/100g) */
  dha_22_6: number;
}

// Minerals (per 100g)
export interface Minerals {
  /** Cholesterol (mg/100g) */
  cholesterol: number;
  /** Sodium chloride salt (g/100g) */
  sodium_chloride: number;
  /** Calcium (mg/100g) */
  calcium: number;
  /** Chloride (mg/100g) */
  chloride: number;
  /** Copper (mg/100g) */
  copper: number;
  /** Iron (mg/100g) */
  iron: number;
  /** Iodine (µg/100g) */
  iodine: number;
  /** Magnesium (mg/100g) */
  magnesium: number;
  /** Manganese (mg/100g) */
  manganese: number;
  /** Phosphorus (mg/100g) */
  phosphorus: number;
  /** Potassium (mg/100g) */
  potassium: number;
  /** Selenium (µg/100g) */
  selenium: number;
  /** Sodium (mg/100g) */
  sodium: number;
  /** Zinc (mg/100g) */
  zinc: number;
}

// Vitamins (per 100g)
export interface Vitamins {
  /** Retinol (µg/100g) */
  retinol: number;
  /** Beta-carotene (µg/100g) */
  beta_carotene: number;
  /** Vitamin D (µg/100g) */
  vitamin_d: number;
  /** Vitamin E (mg/100g) */
  vitamin_e: number;
  /** Vitamin C (mg/100g) */
  vitamin_c: number;
  /** Vitamin B1/Thiamine (mg/100g) */
  vitamin_b1_thiamine: number;
  /** Vitamin B2/Riboflavin (mg/100g) */
  vitamin_b2_riboflavin: number;
  /** Vitamin B3/PP/Niacin (mg/100g) */
  vitamin_b3_niacin: number;
  /** Vitamin B5/Pantothenic acid (mg/100g) */
  vitamin_b5_pantothenic: number;
  /** Vitamin B6 (mg/100g) */
  vitamin_b6: number;
  /** Vitamin B9/Folate (µg/100g) */
  vitamin_b9_folate: number;
  /** Vitamin B12 (µg/100g) */
  vitamin_b12: number;
}

// Complete nutritional data model
export interface NutritionalData extends FoodIdentification {
  macronutrients: Macronutrients;
  sugars: Sugars;
  fatty_acids: FattyAcids;
  minerals: Minerals;
  vitamins: Vitamins;
}

// Hierarchical tree structure for food groups
export interface FoodGroup {
  /** Group code (e.g., "01", "02", etc.) */
  code: string;
  /** Group name (derived from food names or can be manually defined) */
  name: string;
  /** Subgroups within this group */
  subgroups: Map<string, FoodSubGroup>;
}

export interface FoodSubGroup {
  /** Subgroup code (e.g., "0101", "0102", etc.) */
  code: string;
  /** Subgroup name */
  name: string;
  /** Sub-subgroups within this subgroup */
  subsubgroups: Map<string, FoodSubSubGroup>;
}

export interface FoodSubSubGroup {
  /** Sub-subgroup code (e.g., "010301", "010302", etc.) */
  code: string;
  /** Sub-subgroup name */
  name: string;
  /** Foods within this sub-subgroup */
  foods: NutritionalData[];
}

// Root structure containing the entire food database
export interface CiqualDatabase {
  /** All food groups organized hierarchically */
  groups: Map<string, FoodGroup>;
  /** Flat list of all foods for quick access */
  allFoods: NutritionalData[];
  /** Statistics about the database */
  stats: {
    totalGroups: number;
    totalSubgroups: number;
    totalSubsubgroups: number;
    totalFoods: number;
  };
}

export const CIQUAL_DB_KEY = 'db_v1';

// Group name mappings based on Ciqual database structure
export const GROUP_NAMES: Record<string, string> = {
  '00': 'Desserts et produits sucrés',
  '01': 'Plats préparés et conserves',
  '02': 'Fruits et légumes',
  '03': 'Céréales et produits céréaliers',
  '04': 'Viandes, œufs, poissons',
  '05': 'Produits laitiers',
  '06': 'Boissons',
  '07': 'Produits sucrés',
  '08': 'Glaces et sorbets',
  '09': 'Matières grasses',
  '10': 'Sauces, condiments et épices',
  '11': 'Aliments pour bébé',
};

// Raw data structure from Excel (for parsing)
export interface RawNutritionalData extends FoodIdentification {
  'Eau (g/100 g)': string;
  'Protéines, N x facteur de Jones (g/100 g)': string;
  'Protéines, N x 6.25 (g/100 g)': string;
  'Glucides (g/100 g)': string;
  'Lipides (g/100 g)': string;
  'Sucres (g/100 g)': string;
  'Fructose (g/100 g)': string;
  'Glucose (g/100 g)': string;
  'Lactose (g/100 g)': string;
  'Maltose (g/100 g)': string;
  'Saccharose (g/100 g)': string;
  'Amidon (g/100 g)': string;
  'Fibres alimentaires (g/100 g)': string;
  'Cendres (g/100 g)': string;
  'Alcool (g/100 g)': string;
  'Acides organiques (g/100 g)': string;
  'AG saturés (g/100 g)': string;
  'AG monoinsaturés (g/100 g)': string;
  'AG polyinsaturés (g/100 g)': string;
  'AG 4:0, butyrique (g/100 g)': string;
  'AG 6:0, caproïque (g/100 g)': string;
  'AG 8:0, caprylique (g/100 g)': string;
  'AG 10:0, caprique (g/100 g)': string;
  'AG 12:0, laurique (g/100 g)': string;
  'AG 14:0, myristique (g/100 g)': string;
  'AG 16:0, palmitique (g/100 g)': string;
  'AG 18:0, stéarique (g/100 g)': string;
  'AG 18:1 9c (n-9), oléique (g/100 g)': string;
  'AG 18:2 9c,12c (n-6), linoléique (g/100 g)': string;
  'AG 18:3 c9,c12,c15 (n-3), alpha-linolénique (g/100 g)': string;
  'AG 20:4 5c,8c,11c,14c (n-6), arachidonique (g/100 g)': string;
  'AG 20:5 5c,8c,11c,14c,17c (n-3) EPA (g/100 g)': string;
  'AG 22:6 4c,7c,10c,13c,16c,19c (n-3) DHA (g/100 g)': string;
  'Cholestérol (mg/100 g)': string;
  'Sel chlorure de sodium (g/100 g)': string;
  'Calcium (mg/100 g)': string;
  'Chlorure (mg/100 g)': string;
  'Cuivre (mg/100 g)': string;
  'Fer (mg/100 g)': string;
  'Iode (µg/100 g)': string;
  'Magnésium (mg/100 g)': string;
  'Manganèse (mg/100 g)': string;
  'Phosphore (mg/100 g)': string;
  'Potassium (mg/100 g)': string;
  'Sélénium (µg/100 g)': string;
  'Sodium (mg/100 g)': string;
  'Zinc (mg/100 g)': string;
  'Rétinol (µg/100 g)': string;
  'Beta-Carotène (µg/100 g)': string;
  'Vitamine D (µg/100 g)': string;
  'Vitamine E (mg/100 g)': string;
  'Vitamine C (mg/100 g)': string;
  'Vitamine B1 ou Thiamine (mg/100 g)': string;
  'Vitamine B2 ou Riboflavine (mg/100 g)': string;
  'Vitamine B3 ou PP ou Niacine (mg/100 g)': string;
  'Vitamine B5 ou Acide pantothénique (mg/100 g)': string;
  'Vitamine B6 (mg/100 g)': string;
  'Vitamine B9 ou Folates totaux (µg/100 g)': string;
  'Vitamine B12 (µg/100 g)': string;
}
